import {Helmet} from '@dr.pogodin/react-helmet';
import {Dialog, showNotification} from 'platform/components';
import {Box} from 'platform/foundation';

import {useState} from 'react';

import {head, isNil} from 'ramda';

import {usePostServiceOrderIssueNoteMutation} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {testIds, warehouseRoutes} from '@dms/routes';
import {DmsDataGrid, WAREHOUSE_GRID_CODES, handleApiError, logError} from '@dms/shared';

import {composePath, Nullish, useNavigate} from 'shared/web';

import type {RowData} from 'features/eag-grid';

export function ServiceOrderIssueCreate() {
  const navigate = useNavigate();

  const [selectedServiceOrder, setSelectedServiceOrder] = useState<string | Nullish>(null);

  const [postServiceOrderIssueNote, {isLoading: isCreatingServiceOrderIssue}] =
    usePostServiceOrderIssueNoteMutation();

  const handleCreateServiceOrderIssueNote = () => {
    if (isNil(selectedServiceOrder)) {
      return logError('Missing serviceOrderId');
    }

    postServiceOrderIssueNote({
      serviceOrderId: selectedServiceOrder,
    })
      .unwrap()
      .then((result) => {
        showNotification.success(i18n.t('entity.warehouse.notifications.serviceOrderIssueCreated'));
        navigate(
          composePath(warehouseRoutes.serviceOrderIssueDetailOverview, {
            params: {id: result.id},
          })
        );
      })
      .catch(handleApiError);
  };

  const handleOnRowSelectionChange = (rows: RowData[]) => {
    setSelectedServiceOrder(head(rows)?.id);
  };

  const handleDiscard = () => {
    navigate(warehouseRoutes.serviceOrderIssues);
  };

  return (
    <>
      <Helmet title={i18n.t('page.warehouse.labels.newServiceOrderIssue')} />
      <Dialog
        isOpen
        title={i18n.t('page.warehouse.labels.newServiceOrderIssue')}
        size="large"
        onClose={handleDiscard}
        scrollBehavior="inside"
        data-testid={testIds.warehouse.serviceOrderIssueCreate('assignDialog')}
        buttons={[
          {
            type: 'button',
            variant: 'secondary',
            title: i18n.t('general.labels.discard'),
            onClick: handleDiscard,
            isLoading: isCreatingServiceOrderIssue,
          },
          {
            type: 'button',
            title: i18n.t('general.labels.assign'),
            onClick: handleCreateServiceOrderIssueNote,
            isDisabled: isNil(selectedServiceOrder),
            isLoading: isCreatingServiceOrderIssue,
            'data-testid': testIds.warehouse.serviceOrderIssueCreate('assign'),
          },
        ]}
      >
        <Box height={140}>
          <DmsDataGrid
            gridCode={WAREHOUSE_GRID_CODES.issueNoteServiceOrders}
            onRowSelectionChange={handleOnRowSelectionChange}
            data-testid={WAREHOUSE_GRID_CODES.issueNoteServiceOrders}
          />
        </Box>
      </Dialog>
    </>
  );
}
